import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Clock, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, track } from "@/lib/api";
import { PLANS } from "@/constants/testIds";

export default function PlanDetail() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);

  const load = () => api.get(`/plans/${planId}`).then(({ data }) => setPlan(data)).catch(() => navigate("/plans"));
  useEffect(() => { track("plan_opened", { plan: planId }); load(); }, [planId]);

  const enroll = async () => {
    await api.post(`/plans/${planId}/enroll`);
    track("plan_enrolled", { plan: planId });
    toast.success("You're enrolled. One day at a time.");
    load();
  };

  if (!plan) return <div className="h-64 flex items-center justify-center">
    <div className="h-8 w-8 rounded-full border-2 border-[#C89B55] border-t-transparent animate-spin" /></div>;

  const pct = plan.total ? Math.round((plan.done / plan.total) * 100) : 0;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <button data-testid={PLANS.backButton} onClick={() => navigate("/plans")}
        className="flex items-center gap-2 text-[#59535B] hover:text-[#252326]">
        <ArrowLeft className="h-4 w-4" /> All plans
      </button>

      <div className="rounded-3xl p-8 text-[#FCFAF6]" style={{ backgroundColor: "#291B2D" }}>
        <span className="text-[11px] uppercase tracking-[0.2em] font-semibold" style={{ color: plan.accent }}>{plan.season}</span>
        <h1 className="font-heading text-3xl lg:text-4xl font-semibold mt-2">{plan.title}</h1>
        <p className="text-[#F7F2EA]/85 mt-2">{plan.subtitle}</p>
        <div className="mt-5 flex items-center gap-3">
          <div className="flex-1 h-2 bg-white/15 rounded-full overflow-hidden">
            <div className="h-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: plan.accent }} />
          </div>
          <span className="text-sm text-[#F7F2EA]/80">{plan.done}/{plan.total} days</span>
        </div>
        {!plan.enrolled && (
          <button data-testid={PLANS.enrollButton} onClick={enroll}
            className="mt-5 rounded-full bg-[#C89B55] text-[#291B2D] hover:bg-[#B38743] px-6 py-2.5 font-semibold transition-smooth">
            Start this plan
          </button>
        )}
      </div>

      <div className="space-y-3">
        {plan.days.map((d) => (
          <motion.button key={d.day} data-testid={`${PLANS.dayItem}-${d.day}`} onClick={() => navigate(`/lesson/${d.lesson_id}`)}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="w-full text-left flex items-center gap-4 bg-white border border-[#291B2D]/5 rounded-2xl p-5 card-soft hover:-translate-y-0.5 transition-smooth">
            <div className={`h-11 w-11 rounded-2xl flex items-center justify-center font-heading shrink-0 ${
              d.completed ? "bg-[#ECF3EE] text-[#3D6B52]" : "bg-[#291B2D] text-[#FCFAF6]"}`}>
              {d.completed ? <CheckCircle2 className="h-5 w-5" /> : d.day}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#C89B55] font-semibold">Day {d.day}</p>
              <p className="font-heading text-lg leading-snug truncate">{d.title}</p>
              <p className="text-sm text-[#59535B] mt-0.5 line-clamp-1">{d.note}</p>
              <span className="text-xs text-[#8A838E] flex items-center gap-1.5 mt-1"><Clock className="h-3 w-3" /> {d.minutes} min</span>
            </div>
            <ArrowRight className="h-4 w-4 text-[#8A838E] shrink-0" />
          </motion.button>
        ))}
      </div>
    </div>
  );
}
